import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ensureLogin, fetchUsers, createUser, updateUser, deleteUser, getCurrentUser } from '../utils/api';

const Team = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'manager',
    department: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loggedUser, setLoggedUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        await ensureLogin();
        const user = await getCurrentUser();
        setLoggedUser(user);
        setIsAdmin(user.role === 'admin');
        const data = await fetchUsers();
        setUsers(data);
      } catch (err) {
        setError('Falha ao carregar utilizadores');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setCurrentUser({ ...user, password: '' });
      setIsEditing(true);
    } else {
      setCurrentUser({ name: '', email: '', password: '', role: 'manager', department: '' });
      setIsEditing(false);
    }
    setError('');
    setSuccess('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentUser({ name: '', email: '', password: '', role: 'manager', department: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser(prev => ({ ...prev, [name]: value }));
  };

  const reloadUsers = async () => {
    const data = await fetchUsers();
    setUsers(data);
  };

  const handleSave = async () => {
    if (!isAdmin) {
      setError('Apenas administradores podem criar/editar utilizadores');
      return;
    }
    try {
      setError('');
      await ensureLogin();
      if (isEditing) {
        const { id, name, email, password, role, department } = currentUser;
        await updateUser(id, { name, email, ...(password ? { password } : {}), role, department });
        setSuccess('Utilizador atualizado');
      } else {
        const { name, email, password, role, department } = currentUser;
        if (!name || !email || !password) {
          setError('Nome, e-mail e palavra-passe são obrigatórios');
          return;
        }
        await createUser({ name, email, password, role, department });
        setSuccess('Utilizador criado');
      }
      await reloadUsers();
      setTimeout(() => {
        setSuccess('');
        handleCloseDialog();
      }, 1000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao guardar utilizador');
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) {
      setError('Apenas administradores podem eliminar utilizadores');
      return;
    }
    if (window.confirm('Tem a certeza que deseja eliminar este utilizador?')) {
      try {
        await ensureLogin();
        await deleteUser(id);
        setSuccess('Utilizador eliminado');
        await reloadUsers();
        setTimeout(() => setSuccess(''), 2000);
      } catch (err) {
        setError('Erro ao eliminar utilizador');
      }
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Equipa
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Gestão de Utilizadores</Typography>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Novo Utilizador
            </Button>
          )}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Função</TableCell>
              <TableCell>Departamento</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.role === 'admin' ? 'Administrador' : (u.role === 'technician' ? 'Técnico' : 'Gestor')}</TableCell>
                <TableCell>{u.department || '-'}</TableCell>
                <TableCell>
                  {isAdmin && (
                    <>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenDialog(u)}>
                        Editar
                      </Button>
                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(u.id)}>
                        Remover
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">Nenhum utilizador encontrado</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Utilizador' : 'Novo Utilizador'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nome" name="name" value={currentUser.name} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="E-mail" name="email" value={currentUser.email} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="password" label="Palavra-passe" name="password" value={currentUser.password} onChange={handleChange} placeholder={isEditing ? 'Deixe em branco para manter' : ''} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Função</InputLabel>
                <Select name="role" value={currentUser.role} onChange={handleChange} label="Função">
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="manager">Gestor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Departamento" name="department" value={currentUser.department} onChange={handleChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>{isEditing ? 'Guardar' : 'Criar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Team;